import Clipboard from "@react-native-clipboard/clipboard";
import {
    Alert,
    Headline,
    Loading,
    TextInputModal,
    Spacer,
    StyleFunction,
    Text,
    useAlert,
    useConfirm,
    useForm,
    useModal,
    useThemedStyle,
    useToast,
    IconButton,
} from "@venuepos/react-common";
import {
    GQFiskalyAdvanceTssStateMutationVariables,
    GQFiskalyRegisterTssForOrganizationMutation,
    GQFiskalySettings,
    GQFiskalyTss,
    GQIntegrationQuery,
    GQTssStateEnum,
    useFiskalyAdvanceTssStateMutation,
    useFiskalyChangePinForTssMutation,
    useFiskalyRegisterTssForOrganizationMutation,
    useFiskalyTssLazyQuery,
    useIntegrationSettingsLazyQuery,
} from "graphql-sdk";
import { FiskalySettings, FiskalySettingsInput } from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { useHandleMutationError } from "../../../hooks";
import { Divider } from "react-native-paper";
import { View } from "react-native";
import { FiskalySettingsForm } from "./fiskaly-settings-form";
import { FetchResult } from "@apollo/client";
import { NIL } from "uuid";
import { schemaFiskalySettings } from "./fiskaly-form";
import { FiskalyEstablishmentMapping } from "./establishment/mapping";

export function FiskalySettingsTab({
    integration,
}: {
    integration: GQIntegrationQuery["integration"];
}) {
    const toast = useToast();
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const { render } = useModal();
    const alert = useAlert();
    const confirm = useConfirm();

    const { handleMutationError } = useHandleMutationError();
    const [fiskalyAdvanceTssState] = useFiskalyAdvanceTssStateMutation();
    const [changePin] = useFiskalyChangePinForTssMutation();
    const [
        getFiskalyTss,
        { data: tssData, loading: tssLoading, refetch: tssRefetch },
    ] = useFiskalyTssLazyQuery();
    const [registerTSS] = useFiskalyRegisterTssForOrganizationMutation();

    const form = useForm<FiskalySettingsInput>(schemaFiskalySettings, null);
    const [{ values }, { setDefaultValues }] = form;

    const [
        getIntegrationSettings,
        { data, loading, refetch: integrationSettingsRefetch },
    ] = useIntegrationSettingsLazyQuery({
        fetchPolicy: "no-cache",
    });

    const copyText = useCallback(
        (textValue: string) => {
            Clipboard.setString(textValue);
            toast.info(t("backoffice.info.text_copied", "Text copied"));
        },
        [t, toast]
    );

    const handleRegisterTss = useCallback(async () => {
        let registerResponse: FetchResult<GQFiskalyRegisterTssForOrganizationMutation>;
        await handleMutationError(
            async () => {
                registerResponse = await registerTSS();
            },
            t("common.success", "Success!"),
            async () => {
                const pukText =
                    registerResponse.data.fiskalyRegisterTssForOrganization
                        .adminPuk;

                // Notify the user about the Admin PUK.
                await alert(
                    t(
                        "backoffice.integration.fiskaly.tss_registered_title",
                        "Fiskaly: TSS registered"
                    ),

                    <View>
                        <Text>
                            {t(
                                "backoffice.integration.fiskaly.tss_registered_body",
                                "A TSS has been registered for use with this organization. Please note the PUK code and store it somewhere safe. The PUK code is needed for the following operations on the TSS.\n\nThis code will only be shown once and cannot be retrieved if you lose it.\n\nClick the text to copy it."
                            )}
                        </Text>
                        <View style={styles.pukContainer}>
                            <View style={styles.row}>
                                <IconButton
                                    name="copy"
                                    color={styles.iconButton.color}
                                    size="small"
                                    onPress={() => {
                                        copyText(pukText);
                                    }}
                                />
                                <Spacer />
                                <Text
                                    onPress={() => {
                                        copyText(pukText);
                                    }}
                                    style={styles.pukText}
                                >
                                    {pukText}
                                </Text>
                            </View>
                        </View>
                    </View>
                );

                // User was notified about PUK. Now reload data from the server to get new data
                await integrationSettingsRefetch();
            }
        );
    }, [
        alert,
        copyText,
        handleMutationError,
        integrationSettingsRefetch,
        registerTSS,
        styles.iconButton.color,
        styles.pukContainer,
        styles.pukText,
        styles.row,
        t,
    ]);

    const handleAdvanceTssState = useCallback(
        async (nextState: GQTssStateEnum) => {
            if (
                [
                    GQTssStateEnum.Created,
                    GQTssStateEnum.Defective,
                    GQTssStateEnum.Evicted,
                    GQTssStateEnum.Deleted,
                ].includes(nextState)
            ) {
                console.warn(`TSS cannot change to state ${nextState}`);
                return;
            }

            let variables: GQFiskalyAdvanceTssStateMutationVariables = {
                tssState: nextState,
            };

            if (nextState === GQTssStateEnum.Uninitialized) {
                toast.info(
                    t(
                        "backoffice.integration.fiskaly.tss_advance_wait",
                        "The initialization of the TSS can take up to 30 seconds. Please wait."
                    ),
                    { duration: 30, showClose: true }
                );
            } else if (nextState === GQTssStateEnum.Initialized) {
                const pinUserInput = await render(onClose => (
                    <TextInputModal
                        onClose={onClose}
                        title={t(
                            "backoffice.integration.fiskaly.tss_admin_title",
                            "Fiskaly: Authentication"
                        )}
                        label={t(
                            "backoffice.integration.fiskaly.tss_admin_pin",
                            "Enter the Admin PIN for TSS operations"
                        )}
                        description={t(
                            "backoffice.integration.fiskaly.register_pin_explain",
                            "This PIN code is used when the TSS is activated and when clients are activated for this TSS. Be sure to keep both the PIN and the PUK code and store them safely. The PIN code must be at least 6 digits."
                        )}
                    />
                ));

                if (!pinUserInput) {
                    toast.warning(
                        t(
                            "backoffice.integration.fiskaly.no_pin",
                            "No PIN was entered."
                        )
                    );
                    return;
                }

                if (String(pinUserInput).length < 6) {
                    toast.warning(
                        t(
                            "backoffice.integration.fiskaly.pin_validation_length",
                            "The PIN code must be at least 6 digits."
                        )
                    );
                    return;
                }

                const pukUserInput = await render(onClose => (
                    <TextInputModal
                        onClose={onClose}
                        title={t(
                            "backoffice.integration.fiskaly.tss_admin_title",
                            "Fiskaly: Authentication"
                        )}
                        label={t(
                            "backoffice.integration.fiskaly.tss_admin_puk",
                            "Enter the Admin PUK for TSS operations"
                        )}
                        description={t(
                            "backoffice.integration.fiskaly.register_puk_explain",
                            "The Admin PUK code was shown when the TSS was registered. You need this for activating the TSS."
                        )}
                    />
                ));

                if (!pukUserInput) {
                    toast.warning(
                        t(
                            "backoffice.integration.fiskaly.no_puk",
                            "No PUK code entered. Cannot promote TSS."
                        )
                    );
                    return;
                }

                variables = {
                    ...variables,

                    pinCode: pinUserInput,
                    adminPuk: pukUserInput,
                };
            }

            if (nextState === GQTssStateEnum.Disabled) {
                if (
                    !(await confirm(
                        t(
                            "backoffice.integration.fiskaly.disable_tss_title",
                            "Disable Fiskaly TSS?"
                        ),
                        t(
                            "backoffice.integration.fiskaly.disable_tss_body",
                            "Are you REALLY sure you want to disable this TSS?\n\nDisabling this means that the TSS is taken out of use and cannot be reinstated again.\n\nYou will need to register a new TSS if you wish to continue using this integration."
                        )
                    ))
                ) {
                    return;
                }

                const pinUserInput = await render(onClose => (
                    <TextInputModal
                        onClose={onClose}
                        title={t(
                            "backoffice.integration.fiskaly.tss_admin_title",
                            "Fiskaly: Authentication"
                        )}
                        label={t(
                            "backoffice.integration.fiskaly.tss_admin_pin",
                            "Enter the Admin PIN for TSS operations"
                        )}
                        description={t(
                            "backoffice.integration.fiskaly.admin_pin_explain",
                            "This PIN code is used when the TSS is activated or disabled and when clients are activated for this TSS."
                        )}
                    />
                ));

                if (!pinUserInput) {
                    toast.warning(
                        t(
                            "backoffice.integration.fiskaly.no_pin",
                            "No PIN was entered."
                        )
                    );
                    return;
                }

                variables = {
                    ...variables,

                    pinCode: pinUserInput,
                };
            }

            let advanceTssStateResponse;

            await handleMutationError(
                async () => {
                    advanceTssStateResponse = await fiskalyAdvanceTssState({
                        variables,
                    });
                },
                t("common.success", "Success!"),
                async () => {
                    if (
                        advanceTssStateResponse?.data?.fiskalyAdvanceTssState
                            ?.status
                    ) {
                        toast.success(t("common.ok", "OK"));
                    } else {
                        toast.error(
                            t("backoffice.common.error", "An error occurred.")
                        );
                    }

                    await tssRefetch();
                }
            );
        },
        [
            confirm,
            fiskalyAdvanceTssState,
            handleMutationError,
            render,
            t,
            toast,
            tssRefetch,
        ]
    );

    const handleChangePin = useCallback(async () => {
        if (!data) {
            return;
        }

        const pukUserInput = await render(onClose => (
            <TextInputModal
                onClose={onClose}
                title={t(
                    "backoffice.integration.fiskaly.tss_admin_title",
                    "Fiskaly: Authentication"
                )}
                label={t(
                    "backoffice.integration.fiskaly.tss_admin_puk",
                    "Enter the Admin PUK for TSS operations"
                )}
                description={t(
                    "backoffice.integration.fiskaly.register_puk_explain",
                    "The Admin PUK code was shown when the TSS was registered. You need this for activating the TSS."
                )}
            />
        ));

        if (!pukUserInput) {
            toast.warning(
                t(
                    "backoffice.integration.fiskaly.no_puk",
                    "No PUK code entered. Cannot promote TSS."
                )
            );
            return;
        }

        const pinUserInput = await render(onClose => (
            <TextInputModal
                onClose={onClose}
                title={t(
                    "backoffice.integration.fiskaly.tss_admin_title",
                    "Fiskaly: Authentication"
                )}
                label={t(
                    "backoffice.integration.fiskaly.tss_admin_pin",
                    "Enter the Admin PIN for TSS operations"
                )}
                description={t(
                    "backoffice.integration.fiskaly.register_pin_explain",
                    "This PIN code is used when the TSS is activated and when clients are activated for this TSS. Be sure to keep both the PIN and the PUK code and store them safely. The PIN code must be at least 6 digits."
                )}
            />
        ));

        if (!pinUserInput) {
            toast.warning(
                t(
                    "backoffice.integration.fiskaly.no_pin",
                    "No PIN was entered."
                )
            );
            return;
        }

        if (String(pinUserInput).length < 6) {
            toast.warning(
                t(
                    "backoffice.integration.fiskaly.pin_validation_length",
                    "The PIN code must be at least 6 digits."
                )
            );
            return;
        }

        let response;
        await handleMutationError(
            async () => {
                response = await changePin({
                    variables: {
                        integrationId: data.integrationSettings.id,
                        pinCode: pinUserInput,
                        adminPuk: pukUserInput,
                    },
                });
            },
            undefined,
            async () => {
                if (response.fiskalyChangePinForTss?.status) {
                    toast.success(t("common.ok", "OK"));
                } else {
                    toast.error(
                        t("backoffice.common.error", "An error occurred.")
                    );
                }
            }
        );
    }, [changePin, data, handleMutationError, render, t, toast]);

    useEffect(() => {
        if (!data) {
            return;
        }

        const settings = data?.integrationSettings as FiskalySettings;

        if (!settings) {
            return;
        }

        const defaultValues: FiskalySettingsInput = {
            merchantId: settings.merchantId,
            organizationId: settings.organizationId,
            tssId: settings.tssId,
            apiKey: settings.apiKey,
        };

        setDefaultValues(defaultValues);
    }, [data, setDefaultValues]);

    useEffect(() => {
        if (!integration?.id) {
            return;
        }

        getIntegrationSettings({
            variables: { integrationId: integration.id },
        });
    }, [getIntegrationSettings, integration?.id]);

    useEffect(() => {
        if (!values) {
            return;
        }

        if (values.tssId && values.tssId !== NIL) {
            getFiskalyTss({
                variables: { id: values.tssId },
                fetchPolicy: "no-cache",
            });
        }
    }, [getFiskalyTss, values]);

    if (!data || loading || tssLoading) {
        return <Loading />;
    }

    const settings = data.integrationSettings as GQFiskalySettings;

    return (
        <View style={styles.container}>
            <FiskalySettingsForm
                form={form}
                tssData={tssData?.fiskalyTss as GQFiskalyTss}
                onRegisterTss={handleRegisterTss}
                onTssStateAdvance={handleAdvanceTssState}
                onChangePin={handleChangePin}
            />
            <Spacer />
            <Divider />
            <Spacer />
            <Headline size="h4">
                {t(
                    "backoffice.integration.fiskaly.establishment_mapping_headline",
                    "Map cash registers to establishments"
                )}
            </Headline>

            {settings &&
            (!settings.organizationId ||
                settings.organizationId === NIL ||
                !settings.tssId ||
                settings.tssId === NIL) ? (
                <Alert type="warning">
                    {t(
                        "backoffice.integration.fiskaly.establishment_mapping_missing_requirements",
                        "No organization or no TSS is registered yet. Check above and fix this first."
                    )}
                </Alert>
            ) : (
                <FiskalyEstablishmentMapping
                    settings={data.integrationSettings as GQFiskalySettings}
                />
            )}
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        maxWidth: theme.dimensions.maxFormWidth,
    },

    pukText: { fontWeight: "bold", fontSize: 20 },

    pukContainer: {
        padding: theme.spacingScale,
        borderColor: theme.colors.info,
        borderWidth: 1,
        marginTop: theme.spacingScale,
        borderRadius: theme.borderRadius,
    },

    row: {
        ...theme.styles.row,
        alignItems: "center",
    },

    iconButton: {
        color: theme.colors.primary,
    },
});
